import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Payments as PaymentsIcon, TrendingUp, AccountBalanceWallet } from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
} from 'recharts';
import { toast } from 'react-toastify';

import StatCard from '../../components/admin/StatCard';
import { getRevenue, listPayments, recordPayment, RevenueSummary, Payment } from '../../services/adminBilling';
import { getTenantAnalytics, TenantAnalyticsRow } from '../../services/adminAnalytics';

const money = (n: number, ccy = 'EUR') => `${ccy === 'EUR' ? '€' : ''}${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const BillingPage: React.FC = () => {
  const [revenue, setRevenue] = useState<RevenueSummary | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [tenants, setTenants] = useState<TenantAnalyticsRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ tenant_id: '', amount: '', status: 'paid', note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rev, pays, tns] = await Promise.all([getRevenue(12), listPayments(), getTenantAnalytics()]);
      setRevenue(rev);
      setPayments(pays);
      setTenants(tns);
    } catch (e) {
      // interceptor toasts
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.tenant_id || !form.amount) {
      toast.error('Tenant and amount are required');
      return;
    }
    try {
      setSaving(true);
      await recordPayment({ tenant_id: form.tenant_id, amount: parseFloat(form.amount), status: form.status, note: form.note || undefined });
      toast.success('Payment recorded');
      setOpen(false);
      setForm({ tenant_id: '', amount: '', status: 'paid', note: '' });
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !revenue) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Billing</Typography>
          <Typography variant="body2" color="text.secondary">Recurring revenue from active subscriptions and recorded payments.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Record payment</Button>
      </Box>

      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={4}><StatCard label="MRR (active subscriptions)" value={money(revenue.mrr, revenue.currency)} icon={<TrendingUp />} color="#2E7D32" /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Received this month" value={money(revenue.received_this_month, revenue.currency)} icon={<PaymentsIcon />} color="#F26F21" /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Outstanding (pending)" value={money(revenue.outstanding, revenue.currency)} icon={<AccountBalanceWallet />} color="#F5B800" /></Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>Revenue received (monthly)</Typography>
        <Box sx={{ width: '100%', height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenue.monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis fontSize={12} />
              <ReTooltip formatter={(v: any) => money(Number(v), revenue.currency)} />
              <Bar dataKey="received" fill="#2E7D32" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} mb={1}>Recorded payments</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Paid at</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>No payments recorded.</Typography></TableCell></TableRow>
            ) : payments.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.tenant_name || p.tenant_id}</TableCell>
                <TableCell>{money(p.amount, p.currency)}</TableCell>
                <TableCell><Chip size="small" label={p.status} color={p.status === 'paid' ? 'success' : p.status === 'pending' ? 'warning' : 'default'} /></TableCell>
                <TableCell>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell>{p.note || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="pay-tenant">Tenant</InputLabel>
              <Select labelId="pay-tenant" label="Tenant" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
                {tenants.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Amount (EUR)" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel id="pay-status">Status</InputLabel>
              <Select labelId="pay-status" label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {['paid', 'pending', 'failed', 'refunded'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Record'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPage;
